import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import type { POIRatingTagKey } from "../../lib/poi-rating-tags";

type Phase = "idle" | "form" | "thanks";
type GpsPromptState = "none" | "prompt" | "denied";

// Module-level set: tracks which POIs this visitor already rated (resets on refresh)
const ratedPoiIds = new Set<string>();

interface UsePoiRatingOptions {
  poiId: string | undefined;
  poiStatus?: string | null;
  orgSlug: string;
  mapSlug: string;
  gpsActive: boolean;
  isOpen: boolean;
}

export function usePoiRating({
  poiId,
  poiStatus,
  orgSlug,
  mapSlug,
  gpsActive,
  isOpen,
}: UsePoiRatingOptions) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [selectedTags, setSelectedTags] = useState<Set<POIRatingTagKey>>(new Set());
  const [gpsPrompt, setGpsPrompt] = useState<GpsPromptState>("none");

  // Reset when poiId changes or sheet closes
  useEffect(() => {
    setPhase("idle");
    setRating(null);
    setComment("");
    setSelectedTags(new Set());
    setGpsPrompt("none");
  }, [poiId, isOpen]);

  const canRate =
    !!poiId &&
    !ratedPoiIds.has(poiId) &&
    poiStatus !== "temp_closed";

  const toggleTag = useCallback((tag: POIRatingTagKey) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const openForm = useCallback(async () => {
    if (!canRate) return;
    // If GPS already active, open directly
    if (gpsActive) {
      setGpsPrompt("none");
      setPhase("form");
      return;
    }
    // Check geolocation permission state
    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      if (result.state === "denied") {
        setGpsPrompt("denied");
      } else {
        setGpsPrompt("prompt");
      }
    } catch {
      // Fallback: permissions API not supported
      setGpsPrompt("prompt");
    }
  }, [canRate, gpsActive]);

  const submit = useCallback(async () => {
    if (!poiId || rating === null) return;

    // Mark as rated immediately (optimistic)
    ratedPoiIds.add(poiId);
    setPhase("thanks");

    // Auto-dismiss after 2s
    setTimeout(() => setPhase("idle"), 2000);

    // Fire-and-forget POST
    try {
      await api.maps.visitor[":orgSlug"][":mapSlug"].feedback.$post({
        param: { orgSlug, mapSlug },
        json: {
          feedbackType: "poi_review",
          poiId,
          rating,
          comment: comment.trim() || undefined,
          tags: selectedTags.size > 0 ? [...selectedTags].join(",") : undefined,
        },
      });
    } catch {
      // Silent fail — visitor UX shouldn't break on network error
    }
  }, [poiId, rating, comment, selectedTags, orgSlug, mapSlug]);

  return {
    phase,
    canRate,
    gpsPrompt,
    rating,
    setRating,
    comment,
    setComment,
    selectedTags,
    toggleTag,
    openForm,
    submit,
  };
}
